const BLOCK_TAGS =
  'table|thead|tbody|tfoot|tr|th|td|caption|colgroup|ul|ol|li|div|section|article|h1|h2|h3|h4|h5|h6';

const PARAGRAPH_BEFORE_BLOCK = new RegExp(
  `<p(?:\\s[^>]*)?>\\s*(?=<\\/?(?:${BLOCK_TAGS})\\b)`,
  'gi',
);
const PARAGRAPH_AFTER_BLOCK = new RegExp(`(<\\/?(?:${BLOCK_TAGS})\\b[^>]*>)\\s*</p>`, 'gi');
const NESTED_PARAGRAPH_START = /<p(?:\s[^>]*)?>\s*(?=<p(?:\s|>))/gi;
const NESTED_PARAGRAPH_END = /<\/p>\s*(?=<\/p>)/gi;
const EMPTY_PARAGRAPH = /<p(?:\s[^>]*)?>\s*(?:(?:&nbsp;|&#160;)|<br\s*\/?\s*>|\s)*<\/p>/gi;
const IMAGE_TAG = /<img\b(?:[^"'<>]|"[^"]*"|'[^']*')*>/gi;
const IMAGE_ATTRIBUTE = /\s+([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
const SUPPLIER_BLANK_IMAGE = /^(?:\/|\.\/)?files\/img\/blank\.png(?:[?#].*)?$/i;
const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function isLazyImageSource(value: string) {
  if (!value || /^[?#]/.test(value) || SUPPLIER_BLANK_IMAGE.test(value)) return false;
  try {
    const url = new URL(value, 'https://description.invalid/');
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function removeSupplierPlaceholderImages(html: string) {
  return html.replace(IMAGE_TAG, tag => {
    const attributes = [...tag.matchAll(IMAGE_ATTRIBUTE)].map(match => ({
      name: match[1].toLowerCase(),
      value: match[2] ?? match[3] ?? match[4] ?? '',
      raw: match[0],
      index: match.index,
    }));
    const src = attributes.find(attribute => attribute.name === 'src');
    if (!src || !SUPPLIER_BLANK_IMAGE.test(src.value.trim())) return tag;

    // Some suppliers use the same blank image as a lazy-loading stub. Keep their
    // actual image, not the unavailable placeholder; no supplier scripts run here.
    const lazyImage = attributes.find(
      attribute =>
        ['data-src', 'data-original', 'data-lazy-src'].includes(attribute.name) &&
        isLazyImageSource(attribute.value.trim()),
    );
    const hasSrcSet = attributes.some(
      attribute => attribute.name === 'srcset' && attribute.value.trim(),
    );
    if (!lazyImage && !hasSrcSet) return '';

    const replacement = (lazyImage?.value.trim() || TRANSPARENT_IMAGE).replace(/"/g, '&quot;');
    return `${tag.slice(0, src.index)} src="${replacement}"${tag.slice(src.index + src.raw.length)}`;
  });
}

/**
 * Repairs invalid paragraph wrappers emitted by some supplier descriptions.
 * Browsers otherwise expand them into hundreds of empty DOM nodes around tables.
 * Removes their known missing spacer image before SSR or image-proxy rewriting.
 */
export function normalizeProductDescriptionHtml(html?: string | null) {
  if (!html) return '';

  let normalized = removeSupplierPlaceholderImages(html)
    .replace(PARAGRAPH_BEFORE_BLOCK, '')
    .replace(PARAGRAPH_AFTER_BLOCK, '$1');

  for (let index = 0; index < 4; index += 1) {
    const next = normalized.replace(NESTED_PARAGRAPH_START, '').replace(NESTED_PARAGRAPH_END, '');

    if (next === normalized) break;
    normalized = next;
  }

  return normalized.replace(EMPTY_PARAGRAPH, '').trim();
}
